import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  TextField,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useToast } from '../contexts/ToastContext';
import { http } from '../lib/http';

interface CSVManagementProps {
  onDataUpdated?: () => void;
}

const CSVManagement: React.FC<CSVManagementProps> = ({ onDataUpdated }) => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [replaceAll, setReplaceAll] = useState(false);
  const [csvData, setCsvData] = useState('');
  const [importResults, setImportResults] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setLoadingState = (key: string, value: boolean) => {
    setLoading(prev => ({ ...prev, [key]: value }));
  };

  const handleDownload = async (type: 'pois' | 'call-codes') => {
    try {
      setLoadingState(`download-${type}`, true);
      
      const response = await http.get(`/config/admin/${type}/export`, {
        responseType: 'blob'
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess(`${type === 'pois' ? 'POIs' : 'Call codes'} exported successfully`);
    } catch (error) {
      console.error(`Error downloading ${type}:`, error);
      showError(`Failed to download ${type === 'pois' ? 'POIs' : 'Call codes'}`);
    } finally {
      setLoadingState(`download-${type}`, false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      showError('Please select a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setCsvData(content);
      setImportResults(null);
    };
    reader.readAsText(file);
  };

  const handleImport = async (type: 'pois' | 'call-codes') => {
    if (!csvData.trim()) {
      showError('Please upload a CSV file first');
      return;
    }

    try {
      setLoadingState(`import-${type}`, true);
      setImportResults(null);

      const response = await http.post(`/config/admin/${type}/import`, {
        csvData,
        replaceAll
      });

      if (response.data.success) {
        const { inserted, updated, errors } = response.data.data;
        setImportResults({
          type,
          inserted,
          updated,
          errors: errors || []
        });

        showSuccess(`${type === 'pois' ? 'POIs' : 'Call codes'} imported successfully`);
        
        // Trigger data refresh
        if (onDataUpdated) {
          onDataUpdated();
        }
      } else {
        showError(response.data.error || 'Import failed');
      }
    } catch (error: any) {
      console.error(`Error importing ${type}:`, error);
      const errorMessage = error.response?.data?.error || `Failed to import ${type === 'pois' ? 'POIs' : 'Call codes'}`;
      showError(errorMessage);
      
      // Show detailed errors if available
      if (error.response?.data?.details) {
        setImportResults({
          type,
          errors: error.response.data.details
        });
      }
    } finally {
      setLoadingState(`import-${type}`, false);
    }
  };

  const getSampleCSV = (type: 'pois' | 'call-codes') => {
    if (type === 'pois') {
      return `id,code,name,address,box_location,category,description,coordinates_x,coordinates_y,coordinates_z,is_active
"POI001","HOSP","City Hospital","123 Main St","REDWOOD","Medical","Main city hospital",100.5,200.3,10.0,true
"POI002","SCHOOL","Central High School","456 Oak Ave","PROMINENCE","Education","Public high school",150.2,180.7,5.5,true
"POI003","GAS","Shell Station","789 Pine Rd","ARBORFIELD","Commercial","Gas station with convenience store",75.8,120.4,0.0,true`;
    } else {
      return `code,description,box_plan,priority
"ADV","Advisory","Misc - Miscellaneous",5
"AIR1","Air Alert 1 Inflight Standby","Rescue - Aircraft Related",2
"AIR2","Air Alert 2 Inflight Emergency","Rescue - Aircraft Related",1
"AMB","Ambulance Request","Medical - Emergency",2
"FIRE","Fire Report","Fire - Emergency",1`;
    }
  };

  const downloadSample = (type: 'pois' | 'call-codes') => {
    const sampleData = getSampleCSV(type);
    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sample-${type}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const clearData = () => {
    setCsvData('');
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ color: '#eceef1', mb: 3, fontWeight: 'bold' }}>
        CSV Management
      </Typography>

      <Grid container spacing={3}>
        {/* Export Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: '#1c1f25', border: '1px solid #40444b' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#eceef1', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DownloadIcon />
                Export Data
              </Typography>
              <Typography variant="body2" sx={{ color: '#b4b9c0', mb: 3 }}>
                Download current POIs and call codes as CSV files for backup or editing.
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => handleDownload('pois')}
                  disabled={loading['download-pois']}
                  startIcon={loading['download-pois'] ? <CircularProgress size={20} /> : <DownloadIcon />}
                  sx={{
                    color: '#eceef1',
                    borderColor: '#464a52',
                    '&:hover': { borderColor: '#8c9198', backgroundColor: '#2a2a2a' }
                  }}
                >
                  Export POIs
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => handleDownload('call-codes')}
                  disabled={loading['download-call-codes']}
                  startIcon={loading['download-call-codes'] ? <CircularProgress size={20} /> : <DownloadIcon />}
                  sx={{
                    color: '#eceef1',
                    borderColor: '#464a52',
                    '&:hover': { borderColor: '#8c9198', backgroundColor: '#2a2a2a' }
                  }}
                >
                  Export Call Codes
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Import Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: '#1c1f25', border: '1px solid #40444b' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#eceef1', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <UploadIcon />
                Import Data
              </Typography>
              <Typography variant="body2" sx={{ color: '#b4b9c0', mb: 3 }}>
                Upload CSV files to import POIs and call codes. Use the sample files as templates.
              </Typography>

              {/* File Upload */}
              <Box sx={{ mb: 3 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  startIcon={<UploadIcon />}
                  sx={{
                    color: '#eceef1',
                    borderColor: '#464a52',
                    '&:hover': { borderColor: '#8c9198', backgroundColor: '#2a2a2a' }
                  }}
                >
                  Choose CSV File
                </Button>
                {csvData && (
                  <Button
                    variant="text"
                    onClick={clearData}
                    sx={{ color: '#dc5050', ml: 2 }}
                  >
                    Clear
                  </Button>
                )}
              </Box>

              {/* Replace All Option */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={replaceAll}
                    onChange={(e) => setReplaceAll(e.target.checked)}
                    sx={{
                      color: '#8c9198',
                      '&.Mui-checked': { color: '#b4d4b4' }
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: '#b4b9c0' }}>
                    Replace all existing data (⚠️ This will delete all current data)
                  </Typography>
                }
              />

              {/* Import Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => handleImport('pois')}
                  disabled={!csvData || loading['import-pois']}
                  startIcon={loading['import-pois'] ? <CircularProgress size={20} /> : <UploadIcon />}
                  sx={{
                    backgroundColor: '#b4d4b4',
                    color: '#282828',
                    '&:hover': { backgroundColor: '#a4c4a4' },
                    '&:disabled': { backgroundColor: '#8c8c8c', color: '#646464' }
                  }}
                >
                  Import POIs
                </Button>
                
                <Button
                  variant="contained"
                  onClick={() => handleImport('call-codes')}
                  disabled={!csvData || loading['import-call-codes']}
                  startIcon={loading['import-call-codes'] ? <CircularProgress size={20} /> : <UploadIcon />}
                  sx={{
                    backgroundColor: '#b4d4b4',
                    color: '#282828',
                    '&:hover': { backgroundColor: '#a4c4a4' },
                    '&:disabled': { backgroundColor: '#8c8c8c', color: '#646464' }
                  }}
                >
                  Import Call Codes
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sample Files Section */}
        <Grid item xs={12}>
          <Card sx={{ backgroundColor: '#1c1f25', border: '1px solid #40444b' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#eceef1', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon />
                Sample CSV Files
              </Typography>
              <Typography variant="body2" sx={{ color: '#b4b9c0', mb: 3 }}>
                Download sample CSV files to see the required format and structure.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => downloadSample('pois')}
                  startIcon={<DescriptionIcon />}
                  sx={{
                    color: '#eceef1',
                    borderColor: '#464a52',
                    '&:hover': { borderColor: '#8c9198', backgroundColor: '#2a2a2a' }
                  }}
                >
                  Download POI Sample
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => downloadSample('call-codes')}
                  startIcon={<DescriptionIcon />}
                  sx={{
                    color: '#eceef1',
                    borderColor: '#464a52',
                    '&:hover': { borderColor: '#8c9198', backgroundColor: '#2a2a2a' }
                  }}
                >
                  Download Call Code Sample
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Import Results */}
        {importResults && (
          <Grid item xs={12}>
            <Card sx={{ backgroundColor: '#1c1f25', border: '1px solid #40444b' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#eceef1', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon />
                  Import Results
                </Typography>
                
                {importResults.errors.length === 0 ? (
                  <Alert severity="success" sx={{ backgroundColor: '#1a3d1a', color: '#b4d4b4' }}>
                    <Typography variant="body2">
                      Successfully imported {importResults.type === 'pois' ? 'POIs' : 'Call codes'}:
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                      <Chip 
                        label={`${importResults.inserted} inserted`} 
                        color="success" 
                        size="small"
                        sx={{ backgroundColor: '#2d5a2d', color: '#b4d4b4' }}
                      />
                      <Chip 
                        label={`${importResults.updated} updated`} 
                        color="info" 
                        size="small"
                        sx={{ backgroundColor: '#1a3d5a', color: '#b4d4b4' }}
                      />
                    </Box>
                  </Alert>
                ) : (
                  <Alert severity="error" sx={{ backgroundColor: '#3d1a1a', color: '#dc5050' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Import completed with errors:
                    </Typography>
                    <List dense>
                      {importResults.errors.map((error: string, index: number) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <ErrorIcon sx={{ color: '#dc5050', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={error} 
                            sx={{ 
                              '& .MuiListItemText-primary': { 
                                color: '#dc5050', 
                                fontSize: '14px' 
                              } 
                            }} 
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CSVManagement;










